/**
 * Turning screen coordinates into points on the deck.
 *
 * One raycaster and a couple of scratch vectors, reused for every pointer
 * event — allocating per move would churn the heap during a scratch.
 */

import * as THREE from 'three';
import type { HitInfo } from '../three/parts/hits.ts';
import { hitOf } from '../three/parts/hits.ts';
import { hardware } from './hardware.ts';

const ray = new THREE.Raycaster();
const ndc = new THREE.Vector2();
const planeHit = new THREE.Vector3();
const up = new THREE.Vector3(0, 1, 0);

/** Normalised device coordinates for a point in CSS pixels. */
function toNDC(clientX: number, clientY: number): THREE.Vector2 {
  const r = hardware().stage.renderer.domElement.getBoundingClientRect();
  return ndc.set(((clientX - r.left) / r.width) * 2 - 1, -((clientY - r.top) / r.height) * 2 + 1);
}

export interface Pick {
  hit: HitInfo;
  point: THREE.Vector3;
}

/** The deck part under a screen point, if any. */
export function pickAt(clientX: number, clientY: number): Pick | null {
  const { stage, tt } = hardware();
  ray.setFromCamera(toNDC(clientX, clientY), stage.camera);
  const first = ray.intersectObjects(tt.hits, false)[0];
  if (!first) return null;
  const hit = hitOf(first.object);
  return hit ? { hit, point: first.point.clone() } : null;
}

/** Where a pointer ray crosses a horizontal plane at height `y`. */
export function planeAt(y: number, clientX: number, clientY: number): THREE.Vector3 | null {
  ray.setFromCamera(toNDC(clientX, clientY), hardware().stage.camera);
  const plane = new THREE.Plane(up, -y);
  return ray.ray.intersectPlane(plane, planeHit) ? planeHit.clone() : null;
}

export interface ScreenPoint {
  x: number;
  y: number;
  onScreen: boolean;
}

/** CSS-pixel position of a point in deck space. */
export function screenOfWorld(x: number, y: number, z: number): ScreenPoint {
  const { stage } = hardware();
  const v = new THREE.Vector3(x, y, z).project(stage.camera);
  const r = stage.renderer.domElement.getBoundingClientRect();
  return {
    x: r.left + (v.x * 0.5 + 0.5) * r.width,
    y: r.top + (-v.y * 0.5 + 0.5) * r.height,
    onScreen: v.x >= -1 && v.x <= 1 && v.y >= -1 && v.y <= 1 && v.z < 1,
  };
}

/** CSS-pixel position of an object's centre, for driving pointer gestures. */
export function screenOf(object: THREE.Object3D | null | undefined): ScreenPoint | null {
  if (!object) return null;
  object.updateWorldMatrix(true, false);
  const p = new THREE.Vector3().setFromMatrixPosition(object.matrixWorld);
  return screenOfWorld(p.x, p.y, p.z);
}
